import {
  Ref,
} from 'vue';
import {
  FieldHtmlNameUtil, HekateDisplayForm, Trans,
} from 'www-shared';
import hekateBus from '../../../../../../bus';
import AbstractSignupScreen from './AbstractSignupScreen';
import { version } from '../../../../../../../package.json';
import OnSubmitFormFunction from '../../../../../../model/OnSubmitFormFunction';
import UnifiedSignupScreenComponent from '../UnifiedSignupScreenComponent';
import UnifiedSignupSessionInfo from '../session-info/UnifiedSignupSessionInfo';

export default abstract class AbstractSubmitSignupScreen extends AbstractSignupScreen {
  private readonly onSubmitForm: OnSubmitFormFunction;

  protected readonly otherScreenComponents: UnifiedSignupScreenComponent[];

  private readonly isFormSubmitting: Ref<boolean>;

  constructor({
    sessionInfo,
    formDefinition,
    onSubmitForm,
    isBackButtonHidden,
    otherScreenComponents,
    isFormSubmitting,
  }: {
    sessionInfo: Ref<UnifiedSignupSessionInfo>,
    formDefinition: Ref<HekateDisplayForm>,
    onSubmitForm: OnSubmitFormFunction,
    isBackButtonHidden: Ref<boolean>,
    otherScreenComponents: UnifiedSignupScreenComponent[],
    isFormSubmitting: Ref<boolean>,
  }) {
    super({ sessionInfo, formDefinition, isBackButtonHidden });
    this.onSubmitForm = onSubmitForm;
    this.otherScreenComponents = otherScreenComponents;
    this.isFormSubmitting = isFormSubmitting;
  }

  /** The components that are visible if there are no recaptcha errors */
  abstract getThisScreensVisibleComponents(): UnifiedSignupScreenComponent[];

  public getVisibleComponents(): UnifiedSignupScreenComponent[] {
    const { formSubmitStatus } = this.sessionInfo.value;

    if (formSubmitStatus !== 'submit-failed') {
      this.otherScreenComponents.forEach((cmp) => {
        cmp.displayField.componentName = 'hidden-field';
      });
    } else {
      this.otherScreenComponents.forEach((cmp) => {
        cmp.displayField.componentName = cmp.displayField.initialComponentName;
      });
    }

    const result = [
      ...this.otherScreenComponents,
      ...this.getThisScreensVisibleComponents(),
    ];

    return result;
  }

  public getValidatedComponents(): UnifiedSignupScreenComponent[] {
    return this.getVisibleComponents()
      .filter((cmp) => cmp.displayField.displayType !== 'form-field-group');
  }

  public async toNextScreen(): Promise<boolean> {
    this.getField(FieldHtmlNameUtil.ussPath).val = AbstractSignupScreen.signupPath.join(' ');
    this.getField(FieldHtmlNameUtil.telemetryHekateBuild).val = version;
    this.getField(FieldHtmlNameUtil.approveUser).val = 'yes';
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.getField(FieldHtmlNameUtil.planId).val = this.sessionInfo.value.chosenPlan!.planId;
    this.getField(FieldHtmlNameUtil.isEduRenew).val = this.sessionInfo.value.isEduRenew;
    this.getField(FieldHtmlNameUtil.isProUpgrade).val = this.sessionInfo.value.isProUpgrade;

    this.isFormSubmitting.value = true;

    await new Promise((res) => {
      setTimeout(() => res(null), 30);
    });

    this.runSubmit();

    await new Promise((res) => {
      setTimeout(() => res(null), 1500);
    });

    return true;
  }

  private async runSubmit() {
    this.sessionInfo.value.formSubmitStatus = 'submitting';
    hekateBus.addEventListener('form-successfully-submitted', async (evt) => {
      const resolution = await evt.data.formSubmitResolution;
      this.sessionInfo.value.resolutionActions = resolution.successActions;
    });
    try {
      const submitFormResult = await this.onSubmitForm(new Event('uss-submit-signup'));
      if (!submitFormResult.isSuccess) {
        this.sessionInfo.value.formSubmitStatus = 'submit-failed';
      } else {
        this.sessionInfo.value.formSubmitStatus = 'submitted';
        this.sessionInfo.value.completedDetailsScreens.push(this.screenName);
        this.sessionInfo.value.submitFormResult = submitFormResult;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
      this.sessionInfo.value.formSubmitStatus = 'submit-failed';
    }
    this.isFormSubmitting.value = false;
  }

  // eslint-disable-next-line class-methods-use-this
  public getNextButtonLabel(): string {
    return Trans.byValue('Complete Sign up');
  }

  // eslint-disable-next-line class-methods-use-this
  public isSubmitScreen(): boolean {
    return true;
  }

  // eslint-disable-next-line class-methods-use-this
  public getProgress(): number {
    return 85;
  }
}
